import React from "react";
import styled from "styled-components";
import {Button} from "ui-kit/Button";
import {Modal} from "ui-kit/Modal";
import {Input} from "ui-kit/PrimitiveControls/Input";
import {CheckingStatus} from "./hooks/useLabels";
import {FormattedMessage} from "react-intl";
import IntlStore from "stories/IntlStore";

interface CreateLabelModalProps {
  status: CheckingStatus;
  checking: boolean;
  creating: boolean;
  isOpen: boolean;
  onHide: () => void;
  onCreateLabel: (name: string) => void;
  onCheckLabel: (name: string) => void;
  onResetCheckLabel: () => void;
}

const getCheckingStatusText = (status: CheckingStatus): React.ReactNode => {
  switch (status) {
    case CheckingStatus.Available:
      return (
        <FormattedMessage
          id="Labels.CreateLabelModal.CheckingStatus.Available"
          defaultMessage="Label is available"
        />
      );
    case CheckingStatus.NotAvailable:
      return (
        <FormattedMessage
          id="Labels.CreateLabelModal.CheckingStatus.NotAvailable"
          defaultMessage="The label is not available"
        />
      );
    case CheckingStatus.Checking:
    default:
      return (
        <FormattedMessage
          id="Labels.CreateLabelModal.CheckingStatus.Checking"
          defaultMessage="Checking label availability"
        />
      );
  }
};

export const CreateLabelModal: React.FC<CreateLabelModalProps> = props => {
  const { isOpen, onHide, onCreateLabel, onCheckLabel, onResetCheckLabel, status, creating, checking } = props;
  const [name, setName] = React.useState<string>("");

  const onChange = (e: any) => {
    onResetCheckLabel();
    setName(e.target.value);
  };

  return (
    <Modal
      visible={isOpen}
      onHide={onHide}
      header={(
        <FormattedMessage
          id="Labels.CreateLabelModal.LabelCreation"
          defaultMessage="Label creation"
        />
      )}
    >
      <ModalContainer>
        <ModalBody>
          <InputContainer>
            <Input
              name="label"
              label={IntlStore.intl.formatMessage({
                id: "Labels.CreateLabelModal.LabelInput",
                defaultMessage: "label"
              })}
              value={name}
              onChange={onChange}
            />
            <CheckButton loading={checking} disabled={name.length < 1 || name.length > 30} onClick={() => onCheckLabel(name)}>
              <FormattedMessage
                id="Labels.CreateLabelModal.Check"
                defaultMessage="Check"
              />
            </CheckButton>
          </InputContainer>
          <CheckLabelStatus $status={status}>{getCheckingStatusText(status)}</CheckLabelStatus>
        </ModalBody>
        <ModalFooter>
          <Button loading={creating} disabled={status !== CheckingStatus.Available} onClick={() => onCreateLabel(name)}>
            <FormattedMessage
              id="Labels.CreateLabelModal.CreateLabel"
              defaultMessage="Create label"
            />
          </Button>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 200px;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 520px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 20px;
`;

const CheckLabelStatus = styled.div<{ $status: CheckingStatus }>`
  width: 100%;
  height: 54px;
  padding: 18px 16px;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  border-radius: 15px;
  background-color: ${props => {
    switch (props.$status) {
      case CheckingStatus.Available:
        return '#EFFFF1';
      case CheckingStatus.NotAvailable:
        return '#FFBBBB';
      case CheckingStatus.Checking:
      default:
        return '#E9E9E9';
    }
  }};
`;

const InputContainer = styled.div`
  display: flex;
  justify-content: stretch;
  align-items: flex-end;
  gap: 5px;
  width: 100%;
`;

const CheckButton = styled(Button)`
  min-height: 45px;
`;
